import { useState, useCallback } from 'react';
import { debugError } from '../utils/debug';

export interface AppliedDiscount {
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  amount: number;
}

interface UseDiscountCodeOptions {
  /**
   * Initial im globalen ConfiguratorState hinterlegter Rabatt.
   * Wird beim ersten Render übernommen, damit ein zuvor eingegebener
   * Code nach Navigation zurück / vor erhalten bleibt.
   */
  initial?: AppliedDiscount | null;
  /**
   * Callback, um den globalen ConfiguratorState zu synchronisieren.
   * Wird bei Anwenden/Entfernen eines Codes aufgerufen.
   */
  onSync?: (discount: AppliedDiscount | null) => void;
  /**
   * Getter für den aktuellen Nettopreis. Wird zum Validierungs-
   * Zeitpunkt aufgerufen, damit das Backend die richtige Basis
   * für die Mindest-Bestellwert-Prüfung sieht.
   */
  getCurrentNetPrice: () => number;
  /**
   * Optionaler Hook für Erfolg/Fehler-Toasts. Wird vom Parent gesetzt,
   * damit dieser Hook nicht selbst UI rendern muss.
   */
  onSuccess?: (message: string) => void;
}

interface UseDiscountCodeResult {
  discountCode: string;
  setDiscountCode: (code: string) => void;
  appliedDiscount: AppliedDiscount | null;
  discountError: string;
  isValidatingDiscount: boolean;
  /** Prüft einen Code beim WordPress-Backend. */
  validateDiscountCode: (code: string) => Promise<void>;
  /** Entfernt den aktuell angewendeten Rabatt. */
  removeDiscountCode: () => void;
  /**
   * Berechnet den aktuellen Rabattbetrag basierend auf dem aktuellen
   * Nettopreis. Ist wichtig für Prozent-Rabatte, deren Betrag sich mit
   * jeder Konfigurationsänderung ändert.
   */
  calculateCurrentDiscountAmount: () => number;
}

/**
 * Kapselt die komplette Rabattcode-Logik – vorher direkt in
 * Summary.tsx eingebettet, jetzt eigenständig testbar und in
 * anderen Komponenten wiederverwendbar (z.B. Warenkorb-Seiten).
 */
export function useDiscountCode({
  initial,
  onSync,
  getCurrentNetPrice,
  onSuccess,
}: UseDiscountCodeOptions): UseDiscountCodeResult {
  const [discountCode, setDiscountCode] = useState<string>(
    initial?.code || ''
  );
  const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount | null>(
    initial || null
  );
  const [discountError, setDiscountError] = useState<string>('');
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);

  const validateDiscountCode = useCallback(
    async (code: string) => {
      if (!code.trim()) {
        setDiscountError('Bitte geben Sie einen Rabattcode ein.');
        return;
      }
      setIsValidatingDiscount(true);
      setDiscountError('');

      try {
        const nettoPrice = getCurrentNetPrice();
        const response = await fetch('/wp-admin/admin-ajax.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            action: 'skyvision_validate_discount',
            code: code.trim().toUpperCase(),
            order_value: nettoPrice.toString(),
            nonce: (window as unknown as { skyvision_ajax?: { discount_nonce?: string } })
              .skyvision_ajax?.discount_nonce || '',
          }),
        });

        const data = await response.json();

        if (data.success) {
          const discount = data.data.discount as AppliedDiscount;
          setAppliedDiscount(discount);
          setDiscountCode(code.trim().toUpperCase());
          setDiscountError('');
          onSync?.(discount);
          onSuccess?.(`Rabattcode "${code.toUpperCase()}" erfolgreich angewendet!`);
        } else {
          setDiscountError(
            data.data || 'Rabattcode nicht gefunden oder nicht gültig.'
          );
          setAppliedDiscount(null);
        }
      } catch (err) {
        debugError('Fehler bei Rabattcode-Validierung:', err);
        setDiscountError(
          'Fehler bei der Validierung. Bitte versuchen Sie es erneut.'
        );
        setAppliedDiscount(null);
      } finally {
        setIsValidatingDiscount(false);
      }
    },
    [getCurrentNetPrice, onSync, onSuccess]
  );

  const removeDiscountCode = useCallback(() => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
    onSync?.(null);
  }, [onSync]);

  const calculateCurrentDiscountAmount = useCallback((): number => {
    if (!appliedDiscount) return 0;
    if (appliedDiscount.type === 'percentage') {
      const currentNet = getCurrentNetPrice();
      return (currentNet * appliedDiscount.value) / 100;
    }
    return appliedDiscount.value; // Festbetrag bleibt konstant
  }, [appliedDiscount, getCurrentNetPrice]);

  return {
    discountCode,
    setDiscountCode,
    appliedDiscount,
    discountError,
    isValidatingDiscount,
    validateDiscountCode,
    removeDiscountCode,
    calculateCurrentDiscountAmount,
  };
}
